import { isValid, parseISO, parseJSON } from "date-fns";
import { z } from "zod";

export const RtNotEmptyString = z
  .string()
  .refine((value) => value.trim().length > 0, {
    message: "Should not be empty",
  });

// Branded type, see https://github.com/colinhacks/zod/issues/3#issuecomment-794459823
export const RtFraction = z
  .string()
  .regex(/^-?\d+(\/\d+)?$/)
  .brand("fraction");

export const RtJsonDate = z
  .string()
  .refine((str) => isValid(parseJSON(str)))
  .brand("jsonDate");

/**
 * A year-month in the ISO-8601 calendar system, such as 2007-12.
 *
 * This is a convenient way to "mark" a string as being a valid year month
 * (without time nor timezone).
 */
export const RtYearMonth = z
  .string()
  .regex(/^\d{4}-\d{2}$/)
  .refine((str) => isValid(parseISO(str)))
  .brand("yearMonth");

export const RtPositiveInteger = z
  .number()
  .nonnegative()
  .int()
  .brand("positiveInteger");

/**
 * A positive integer excluding 0.
 *
 * Useful to describe quantities.
 */
export const RtCountableNumber = z
  .number()
  .min(1)
  .int()
  .brand("countableNumber");

export const RtWeekCount = z
  .number()
  .nonnegative()
  .int()
  .max(52)
  .brand("weekCount");

export const RtRichTextData = z.strictObject({
  ops: z.array(
    z.object({
      insert: z.union([z.string(), z.record(z.unknown())]).optional(),
      delete: z.number().optional(),
      retain: z.number().optional(),
      attributes: z.record(z.unknown()).optional(),
    }),
  ),
});

const RtRelativesRecord = <T extends [string, ...Array<string>]>(
  enumKeys: z.ZodEnum<T>,
) => {
  const RtNullableFraction = RtFraction.nullable();

  return z.strictObject(
    Object.fromEntries(
      enumKeys.options.map((key) => [key, RtNullableFraction]),
    ) as Record<T[number], typeof RtNullableFraction>,
  );
};

export const RtWithName = z.object({
  name: z.string(),
});

export const RtWithValue = <TSchema extends z.ZodTypeAny>(schema: TSchema) =>
  z.object({
    value: schema.nullable(),
  });

export const RtWithRelativesSet = <T extends [string, ...Array<string>]>(
  enumKeys: z.ZodEnum<T>,
) =>
  z.object({
    relatives: RtRelativesRecord(enumKeys),
  });

export const RtWithProjectionPeriods = z.object({
  /**
   * The projection for each period expressed in a portion of the total amount.
   */
  projectionPeriods: z.array(RtFraction.nullable()),
});

export const RtFundingType = z.enum(["debtFunded", "forwardFunded"]);

export const RtWithFundingType = z.object({
  fundingType: RtFundingType,
});

export const RtPeriodGranularity = z.enum(["annual", "monthly", "weekly"]);

export const RtApraoErrorNames = z.enum([
  "VersionMismatch",
  "MissingInput",
  "ParseError",
  "ValidationError",
  "BadRequest",
  "Unauthorized",
  "Forbidden",
  "NotFound",
  "Conflict",
  "ConcurrentUpdateConflict",
  "InternalServerError",
]);
/**
 * An Aprao specific format of error data.
 *
 * It is returned by the API in case of error to be read by the client.
 */
export const RtApraoHttpErrorData = z.object({
  name: RtApraoErrorNames,
  details: z.unknown(),
});
